import { saneOption, Scope } from 'logard';
import { reactive } from 'vue';
import VehicleStatus from '../dossier/constants/VehicleStatus';
import WorkStatus from '../dossier/constants/WorkStatus';
import WorkshopTeam, { WorkshopTeamId } from '../general/models/WorkshopTeam';
import AuthService from '../general/services/AuthService';
import CommonFilterType from '../shared/constants/CommonFilterType';
import { parseNumericId } from '../shared/converters/commonConverters';
import { ContextLoader } from '../shared/models/AbstractContext';
import Brand, { BrandId } from '../shared/models/Brand';
import FilterService from '../shared/services/FilterService';
import { extractIds } from '../shared/utils';
import { BRANDS_STORAGE_KEY, saveMultiBrandFilter, saveMultiTeamFilter, TEAMS_STORAGE_KEY } from '../shared/utils/filters';
import { saneEnum, saneNumericId } from '../shared/utils/sanitizers';
import { enumValues, isPresent } from '../shared/utils/typescript';
import StatusBoardGrouping from './constants/StatusBoardGrouping';
import { VEHICLE_STATUS_QUERY_PARAM, WORK_STATUS_QUERY_PARAM } from './constants/params';
import StatusBoardContext from './models/StatusBoardContext';
import StatusBoardFilters from './models/StatusBoardFilters';
import StatusBoardService from './services/StatusBoardService';

let context: StatusBoardContext | null = null;

interface FilterData {
  workStatuses: Array<WorkStatus>;
  vehicleStatuses: Array<VehicleStatus>;
}

const COMMON_FILTERS = [CommonFilterType.USER_MULTIPLE, CommonFilterType.GROUP_MULTIPLE, CommonFilterType.OUTLET_MANDATORY, CommonFilterType.DOSSIER_TAG];

const FILTER_STORAGE_KEY = 'statusBoardFilters';

export const TEAMS_QUERY_PARAM = 'teamIds';
export const BRANDS_QUERY_PARAM = 'brandIds';

export const statusBoardLoader = new ContextLoader(async s => {
  const user = AuthService.getCurrentUser();
  const data = loadFilterData();

  const settings = await StatusBoardService.parseSettings(s);

  const common = await FilterService.getFilters(s, COMMON_FILTERS);

  const outletIds = extractIds(common.getActiveOutlets());

  const teams = user.getAvailableTeams(outletIds);
  const teamIds = getTeamIds(s, teams);

  const brands = user.getAvailableBrands(outletIds);
  const brandIds = getBrandIds(s, brands);

  let workStatuses = data?.workStatuses ?? enumValues(WorkStatus);
  let vehicleStatuses = data?.vehicleStatuses ?? enumValues(VehicleStatus);

  if (settings.grouping === StatusBoardGrouping.WORK_STATUS) {
    vehicleStatuses = s.getQueryParams(VEHICLE_STATUS_QUERY_PARAM, saneEnum(VehicleStatus), vehicleStatuses);
    s.removeQueryParams(WORK_STATUS_QUERY_PARAM);
  } else {
    workStatuses = s.getQueryParams(WORK_STATUS_QUERY_PARAM, saneEnum(WorkStatus), workStatuses);
    s.removeQueryParams(VEHICLE_STATUS_QUERY_PARAM);
  }

  const filters = new StatusBoardFilters(common, teams, teamIds, brands, brandIds, workStatuses, vehicleStatuses);
  saveFilterData(filters);
  const newContext = new StatusBoardContext(filters, settings);
  context ? Object.assign(context, newContext) : context = reactive(newContext);
  await StatusBoardService.loadInitial(context);

  // Temporary fix for the new checked in status
  if (context) {
    context.columns = context.columns.filter(
      (column) => column.status !== VehicleStatus.CHECKED_IN_NEW
    );
  }

  return context;
});

function loadFilterData(): FilterData | undefined {
  const json = sessionStorage.getItem(FILTER_STORAGE_KEY);
  if (json) return JSON.parse(json);
}

function saveFilterData(data: StatusBoardFilters): void {
  sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(data));
}

function getTeamIds(scope: Scope, teams: Array<WorkshopTeam>): Array<WorkshopTeamId> {
  const oldValue = sessionStorage.getItem(TEAMS_STORAGE_KEY)
    ?.split(',')
    .map(teamId => parseNumericId<WorkshopTeamId>(teamId))
    .filter(isPresent) ?? [];

  const options = teams.map(t => t.id);

  if (teams.length) {
    const fallback = teams.length > 1 ? oldValue : options;
    return scope.getQueryParams(TEAMS_QUERY_PARAM, saneOption(options, saneNumericId<WorkshopTeamId>), fallback);
  } else {
    scope.removeQueryParams(TEAMS_QUERY_PARAM);
    return [];
  }
}

function getBrandIds(scope: Scope, brands: Array<Brand>): Array<BrandId> {
  const oldValue = sessionStorage.getItem(BRANDS_STORAGE_KEY)
    ?.split(',')
    .map(brandId => parseNumericId<BrandId>(brandId))
    .filter(isPresent) ?? [];

  const options = brands.map(t => t.id);

  if (brands.length) {
    const fallback = brands.length > 1 ? oldValue : options;
    return scope.getQueryParams(BRANDS_QUERY_PARAM, saneOption(options, saneNumericId<BrandId>), fallback);
  } else {
    scope.removeQueryParams(BRANDS_QUERY_PARAM);
    return [];
  }
}